import { DataGridGroupingClient, OnGridReadyEventFn } from '@cmg/common';
import type { GridReadyEvent } from 'ag-grid-community';
import isNil from 'lodash/isNil';
import React from 'react';

import { CrmSelectionRow } from '../CrmSelectModal.model';
import { CrmSelectGridColumns } from './CrmSelectColumns';
import { SCmgEntityName, SCmgEntityNameWrapper, SGridWrapper } from './CrmSelectGrid.styles';

export type Props = {
  isLoading: boolean;
  rows: CrmSelectionRow[];
  onGridReady: OnGridReadyEventFn;
  firmKey?: string | null;
  isSyndicate?: boolean;
};

const groupHeaderRenderer = (groupValue: string | null) =>
  groupValue ? (
    <SCmgEntityNameWrapper>
      CMG entity name: <SCmgEntityName>{groupValue}</SCmgEntityName>
    </SCmgEntityNameWrapper>
  ) : null;

const CrmSelectGrid: React.FC<Props> = ({ isLoading, rows, onGridReady, firmKey, isSyndicate }) => {
  const updatedRows = React.useMemo(() => {
    if (!rows.some(row => isNil(row.id)) && isSyndicate) {
      return [
        {
          id: null,
          firmName: 'Unassign',
          cmgEntityName: '',
          internalSalesCoverage: null,
          location: '',
        },
        ...rows,
      ];
    }
    return rows;
  }, [isSyndicate, rows]);

  return (
    <SGridWrapper>
      <DataGridGroupingClient<CrmSelectionRow>
        loading={isLoading}
        rows={updatedRows}
        columns={CrmSelectGridColumns}
        onGridReady={(event: GridReadyEvent) => {
          event.api.forEachNode(node => node.setSelected(node.data.id === firmKey));
          onGridReady(event);
        }}
        resizeBy="grid"
        resizeStrategy="fit-content"
        domLayout="normal"
        extended={{
          withMargin: false,
          hideColumnResize: true,
          hideColumnSelector: true,
          fillViewport: false,
        }}
        groupByField="cmgEntityName"
        onFirstDataRendered={({ api }) => {
          api.sizeColumnsToFit();
        }}
        groupHeaderRenderer={groupHeaderRenderer}
        gridOptions={{
          suppressRowClickSelection: false,
          getRowHeight: params => {
            if (params.data.id === '') {
              return 1;
            }
            return params.data['__groupHeader'] ? 24 : 40;
          },
        }}
      />
    </SGridWrapper>
  );
};

export default CrmSelectGrid;
